import { createContext, useCallback, useContext, useState } from 'react'

import { flattenFields } from '../utilities/fieldUtils'
import useLocalStorage from '../hooks/useLocalStorage'

const MetaContext = createContext(null)

export const useEntityMeta = (endpoint) => {
  const ctx = useContext(MetaContext)
  if (!ctx) throw new Error('useEntityMeta must be used within MetaProvider')
  return ctx.store[endpoint] ?? null
}

export const useSetMeta = () => useContext(MetaContext).setMeta

export const useDraft = (endpoint) => {
  const ctx = useContext(MetaContext)
  if (!ctx) throw new Error('useDraft must be used within MetaProvider')
  return ctx.drafts[endpoint] ?? null
}

export const useSetDraft = () => useContext(MetaContext).setDraft
export const useClearDraft = () => useContext(MetaContext).clearDraft

export const MetaProvider = ({ children }) => {
  const [store, setStore] = useState({})
  const [drafts, setDrafts] = useLocalStorage('newEntityDrafts', {})

  const setMeta = useCallback((endpoint, rawMeta) => {
    setStore((prev) => {
      if (
        prev[endpoint] &&
        JSON.stringify(prev[endpoint].meta) === JSON.stringify(rawMeta)
      )
        return prev
      const fields = flattenFields(rawMeta.sections)
      return {
        ...prev,
        [endpoint]: {
          meta: rawMeta,
          fields,
        },
      }
    })
  }, [])

  const setDraft = useCallback(
    (key, data) => {
      setDrafts((prev) => ({ ...prev, [key]: data }))
    },
    [setDrafts],
  )

  const clearDraft = useCallback(
    (key) => {
      setDrafts((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    },
    [setDrafts],
  )

  return (
    <MetaContext.Provider
      value={{ store, setMeta, drafts, setDraft, clearDraft }}
    >
      {children}
    </MetaContext.Provider>
  )
}
